import { useContext, useState } from "react"
import { useMutation, useQuery, useLazyQuery, gql } from "@apollo/client"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Formik, Form } from "formik"
import _ from "lodash"
import { trimTextValue } from "../../components/FieldTypes/helpers/trimTextValue"

// Loaders
import { LoaderMedium } from "../../components/Loaders"

// Helpers
import fieldTypesDefault from "./fieldTypesDefault"
import handleInitialValues from "./helpers/handleInitialValues"
import fieldValidationSchema from "./helpers/fieldValidationSchema"
import handleFieldConditional from "./helpers/handleFieldConditional"
import handleSubmitValues from "./helpers/handleSubmitValues"
import getFieldTypeOptions from "./helpers/getFieldTypeOptions"
import getFieldSubtypeOptions from "./helpers/getFieldSubtypeOptions"
import { EDIT_FIELD, GET_CUSTOM_FIELD, GET_FIELDS_LIBRARY } from "./queries"

// Comps
import TypeSelectOptions from "./components/TypeSelectOptions"
import FieldHelpText from "./components/FieldHelpText"

// Context
import { UserContext } from "../../context/userContext"

// Prime
import { InputText as Input } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { SelectButton } from "primereact/selectbutton"

// Styles
import styles from "../../styles/Form.module.css"

export default function LibraryEditCustomField() {
  const userCtx = useContext(UserContext)
  const [isUnique, setIsUnique] = useState(true)
  const [fieldNameReport, setFieldNameReport] = useState("")
  const navigate = useNavigate()

  const [editField] = useMutation(EDIT_FIELD)
  const { idField } = useParams()

  const { loading, data } = useQuery(GET_CUSTOM_FIELD, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      idField,
    },
    skip: !userCtx,
    fetchPolicy: "no-cache",
  })

  const CHECK_NAME_REPORT = gql`
    query CheckNameReport($enterprise_id: ID!, $field_name_report: String!) {
      getNameReport: checkNameReport(
        enterprise_id: $enterprise_id
        field_name_report: $field_name_report
      ) {
        data
      }
    }
  `

  const [checkNameReport, { loadingField }] = useLazyQuery(CHECK_NAME_REPORT, {
    fetchPolicy: "no-cache",
  })

  const dateFieldSubTypes = getFieldSubtypeOptions("date")

  if (loading) return <LoaderMedium />

  // RSI required
  const rsi_required = data?.cloneField?.rsi_required

  const handleFieldType = (formik) => {
    const { field_type } = formik.initialValues
    const fieldTypeOptions = getFieldTypeOptions(field_type)
    const isSysField = field_type === "sys" || rsi_required

    if (_.isEmpty(fieldTypeOptions)) {
      const effectiveFieldTypes = isSysField
        ? [...fieldTypesDefault, { label: "System", value: "sys" }]
        : fieldTypesDefault

      return (
        <div className={styles.field}>
          <label className={styles.label}>Field type:</label>
          <FieldHelpText
            message={`${effectiveFieldTypes.find((type) => type.value === field_type)?.label || "Some"} fields may not have their field type changed.`}
          />
          <Dropdown
            disabled
            name="field_type"
            inputId="field_type"
            value={formik.values.field_type}
            options={effectiveFieldTypes}
            className={styles.value}
          />
        </div>
      )
    } else {
      return (
        <div className={styles.field}>
          <label className={styles.label}>Field type:</label>
          {isSysField && (
            <FieldHelpText message="System fields may not have their field type changed." />
          )}
          <Dropdown
            name="field_type"
            inputId="field_type"
            value={formik.values.field_type}
            options={fieldTypeOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={styles.value}
            disabled={isSysField}
          />
          {formik.touched.field_type && formik.errors.field_type && (
            <div className={styles.errFieldMsg}>{formik.errors.field_type}</div>
          )}
        </div>
      )
    }
  }

  const editMode = true
  const initialValues = handleInitialValues(data, editMode)

  return (
    <div className="layout-full">
      <h1>Edit Field</h1>
      <Link
        to="/enterprise/library"
        style={{ marginBottom: 30, display: "inline-block" }}
        className="btn-sm"
      >
        Back to Library
      </Link>
      <Formik
        initialValues={initialValues}
        validationSchema={fieldValidationSchema}
        onSubmit={async (values) => {
          await editField({
            variables: {
              idField,
              organization_id: userCtx.session.user.organization_id,
              enterprise_id: userCtx.session.user.enterprise_id,
              updated_by: userCtx.session.user.staff_id,
              ...handleSubmitValues(values),
            },
            refetchQueries: [
              {
                query: GET_FIELDS_LIBRARY,
                variables: {
                  staff_id: userCtx.session.user.staff_id,
                  organization_id: userCtx.session.user.organization_id,
                },
              },
            ],
          })
          navigate("/enterprise/library")
        }}
      >
        {(formik) => {
          if (fieldNameReport === "") {
            setFieldNameReport(formik.values.field_name_report)
          }

          return (
            <Form className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>System field name:</label>
                <Input
                  name="field_name"
                  value={formik.values.field_name}
                  disabled
                  className={styles.value}
                />
              </div>
              <div className={styles.field}>
                {loadingField && <i className="pi pi-spin pi-spinner" />}
                <label className={styles.label}>Reporting field name:</label>
                <Input
                  name="field_name_report"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={async (e) => {
                    checkNameReport({
                      variables: {
                        enterprise_id: userCtx.session.user.enterprise_id,
                        field_name_report: formik.values.field_name_report,
                      },
                      onCompleted: ({ getNameReport: { data } }) => {
                        if (!_.isEmpty(data)) {
                          const fieldsList = JSON.parse(data)
                          const sameField = _.find(fieldsList, {
                            field_name_report: fieldNameReport,
                          })
                          if (!_.isEmpty(sameField)) {
                            setIsUnique(true)
                          } else {
                            setIsUnique(false)
                            formik.setFieldValue("field_name_report", "")
                          }
                        } else {
                          setIsUnique(true)
                        }
                      },
                    })
                  }}
                  value={formik.values.field_name_report}
                  className={styles.value}
                />
              </div>
              {!isUnique && !loadingField && (
                <div className={styles.errFieldMsg}>
                  Field name already in use. Please choose another custom
                  reporting field name
                </div>
              )}
              <div className={styles.field}>
                <label className={styles.label}>Is required:</label>
                <SelectButton
                  name="field_required"
                  value={formik.values.field_required === true ? "Yes" : "No"}
                  onChange={(e) => {
                    const value = e.value === "Yes" ? true : false
                    formik.setFieldValue("field_required", value)
                  }}
                  onBlur={formik.handleBlur}
                  options={["No", "Yes"]}
                  aria-label="field_required"
                  className={styles.value}
                />
              </div>

              {formik.values.field_required && (
                <div className={styles.field}>
                  <label className={styles.label}>Message when required:</label>
                  <Input
                    name="field_required_msg"
                    onChange={(e) => formik.handleChange(trimTextValue(e))}
                    onBlur={formik.handleBlur}
                    value={formik.values.field_required_msg}
                    className={styles.value}
                  />
                  {formik.touched.field_required_msg &&
                    formik.errors.field_required_msg && (
                      <div className={styles.errFieldMsg}>
                        {formik.errors.field_required_msg}
                      </div>
                    )}
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Data Dictionary:</label>
                <InputTextarea
                  type="textarea"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={styles.valueW75}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Field label that displays on forms:
                </label>
                <Input
                  name="field_label"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={formik.handleBlur}
                  value={formik.values.field_label}
                  className={styles.value}
                />
                {formik.touched.field_label && formik.errors.field_label && (
                  <div className={styles.errFieldMsg}>
                    {formik.errors.field_label}
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>
                  Description for data entry on forms:
                </label>
                <InputTextarea
                  name="field_description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.field_description}
                  className={styles.valueW75}
                />
                {formik.touched.field_description &&
                  formik.errors.field_description && (
                    <div className={styles.errFieldMsg}>
                      {formik.errors.field_description}
                    </div>
                  )}
              </div>

              {handleFieldType(formik)}

              {formik.values.field_type === "date" && (
                <div className={styles.field}>
                  <label className={styles.label}>Field sub-type:</label>
                  {Boolean(formik.values.field_type_subtype) && (
                    <FieldHelpText message="Date fields may not have their field sub-type changed." />
                  )}
                  <Dropdown
                    name="field_type_subtype"
                    value={formik.values.field_type_subtype}
                    options={dateFieldSubTypes}
                    className={styles.value}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={Boolean(formik.values.field_type_subtype)}
                  />
                </div>
              )}

              <TypeSelectOptions rsi_required={rsi_required} />

              <div className={styles.field}>
                <label className={styles.label}>Is conditional:</label>
                <SelectButton
                  name="field_conditional"
                  value={
                    formik.values.field_conditional === true ? "Yes" : "No"
                  }
                  onChange={(e) => {
                    const value = e.value === "Yes" ? true : false
                    formik.setFieldValue("field_conditional", value)
                  }}
                  onBlur={formik.handleBlur}
                  options={["No", "Yes"]}
                  aria-label="field_conditional"
                  className={styles.value}
                />
              </div>

              {handleFieldConditional(formik)}

              <Button
                className={styles.btnSubmit}
                label="Submit"
                icon="pi pi-check"
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                type="submit"
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
